import { makeStateStore } from '@playcastdotio/StateStore';
import type { StateStore } from '@playcastdotio/CoreTypes';
import { generators } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { IReplicationRouter } from '../../ReplicationRouter';
import type { DeviceInfo } from '../stores';

export type DisplaySourceState = {
  selectedDevice: string;
  enabled: boolean;
  windowCapturePid: number | undefined;
  cameras: DeviceInfo[];
  runningApps: { name: string; processId: number }[];
};

export type DisplaySourceDeps = {
  ipcService: IIpcService;
  getHostAuthId: () => string;
  getActiveShadowcams: () => string[];
};

export class DisplaySourceManager {
  private store: StateStore<DisplaySourceState>;
  private deps: DisplaySourceDeps;

  constructor(deps: DisplaySourceDeps) {
    this.deps = deps;
    this.store = makeStateStore<DisplaySourceState>({
      selectedDevice: 'display',
      enabled: true,
      windowCapturePid: undefined,
      cameras: [],
      runningApps: [],
    });
  }

  public getStore(): StateStore<DisplaySourceState> {
    return this.store;
  }

  public registerReplicationHandler(router: IReplicationRouter): void {
    router.register('runningApplicationState', this.onRunningAppsReplication.bind(this));
    console.log('[DisplaySourceManager] Registered replication handler for runningApplicationState');
  }

  public selectDesktopDisplay(): void {
    this.setDisplaySurfing(false);
    this.disconnectProcess();
    this.updateDevice('display');
  }

  public selectSurfMode(): void {
    this.setDisplaySurfing(true);
    this.disconnectProcess();
    this.updateDevice('followMouse');
  }

  public selectDesktopCapture(): void {
    this.setDisplaySurfing(false);
    this.disconnectProcess();
    this.updateDevice('desktopCapture');
  }

  public selectWindowCapture(processId: number): void {
    const hostAuthId = this.deps.getHostAuthId();

    this.deps.ipcService.send(
      generators.process.processSetPolicy(
        hostAuthId,
        false, true, true, false, 60000, true, true, true, true,
      ),
    );
    this.deps.ipcService.send(
      generators.process.processSelect(hostAuthId, processId),
    );
    this.setDisplaySurfing(false);

    this.store.updateState({
      ...this.store.currentState,
      selectedDevice: 'display',
      enabled: true,
      windowCapturePid: processId,
    });
  }

  public selectCamera(deviceId: string): void {
    this.disconnectProcess();
    this.updateDevice(deviceId);
  }

  public refreshRunningApps(): void {
    const hostAuthId = this.deps.getHostAuthId();
    this.deps.ipcService.send(generators.process.processEnumRunning(hostAuthId));
  }

  public disconnectProcess(): void {
    const hostAuthId = this.deps.getHostAuthId();
    this.deps.ipcService.send(generators.process.processDisconnect(hostAuthId));
  }

  public onCamerasChanged(cameras: DeviceInfo[]): void {
    this.store.updateState({
      ...this.store.currentState,
      cameras,
    });
  }

  private setDisplaySurfing(enabled: boolean): void {
    const hostAuthId = this.deps.getHostAuthId();
    this.deps.ipcService.send(generators.camera.setPicking(enabled, hostAuthId));
    const activeCams = this.deps.getActiveShadowcams();
    activeCams.forEach((cam) => {
      this.deps.ipcService.send(generators.camera.autoPick(cam, enabled, hostAuthId));
    });
  }

  private updateDevice(deviceId: string): void {
    this.store.updateState({
      ...this.store.currentState,
      selectedDevice: deviceId,
      enabled: deviceId !== '0',
      windowCapturePid: undefined,
    });
  }

  private onRunningAppsReplication(state: unknown): void {
    if (!Array.isArray(state)) return;
    const runningApps = (state as [string, { processId: number }][]).map(
      ([name, info]) => ({ name, processId: info.processId }),
    );
    this.store.updateState({
      ...this.store.currentState,
      runningApps,
    });
  }
}
